import { Vector3, Color4 } from '@babylonjs/core';

export const COMPONENT_TYPES = {
	FLUSH_DORMER: 'flush_dormer',
};

export const constants = {
	cornerWidth: 4,
	cornerDepth: 8,
	deluxeDepth: 4,
	deluxeFullDepth: 16,
	middleWidth: 8,
	middleDepth: 4,
	balusterToSide: 0.192, //Distance from baluster to side wall
	cabinPorchDepth: 4,
	shuttersBox: '_shutters_box_obj',
	frameName: 'frame_',
	frameTopName: '_top',
	cutBlock: '_cut_block_obj',
	bakeSuffixCutObj: '__bake',
	finalSuffixCutObj: '__final',
	frameComponent: '__frame__',
	exteriorOffset: 0.3,
	interiorOffset: 0.1,
	doubleGablePorchDepth: 4,
	singleGablePorchDepth: 4,
	balusterCloneKey: 'balusterClones',
	postCloneKey: 'postClones',
	rafterCloneKey: 'rafterClones',
	beamCloneKey: 'beamClones',
	initRoofGableDegrees: 'initRoofGableDegrees',
	initLowerRoofDegrees: 'initLowerRoofDegrees',
	initUpperRoofDegrees: 'initUpperRoofDegrees',
	initSecondFloorHeight: 'initSecondFloorHeight',
	topHeight: 'topHeight',
	innerWallThickness: 0.04,
	windowTopBufferFt: 0.5,
	windowBoundsEpsilonFt: 0.001,
	moveAlongFloor: 'move_along_floor',
	moveAlongInteriorWall: 'move_along_interior_wall',
	tackDoorInfo: 'tackDoorInfo',
	accentGableGroup: 'group-upgrade-upgrade-accent-gable',
	accentGableName: 'ssb-group-upgrade-accent-gable',
	isEnableAccentGable: 'isEnableAccentGable',
	shedWall: 'shedWall',
	cndURL: 'cndURL',
	componentMeshes: 'componentsMeshes',
	orientationEnd: 'end',
	orientationSide: 'side',
	originShedTypeVersion: 'originShedTypeVersion',
	wallInsulationLabel: 'wall_insulation',
	roofInsulationLabel: 'roof_insulation',
	partialWall: 'partial_wall',
	interiorWall: 'interior_wall',
	keyMaterial: 'material',
	keyColor: 'color',
	porchGroupLabel: 'porch_group',
	porchNameLabel: 'porch_name',
	withRailingNameLabel: 'with_railing',
	noRailingNameLabel: 'no_railing',
	braceOffset: 0.807,
	hardCodeOpening: 'left',
	corner: '__corner',
	gableEnd: '__gable_end',
	side: '__side',
	post: 'post',
	brace: 'brace',
	postItem: 'post_item',
	initPostItem: 'post_item_clone_obj',
	initBrace: 'post_brace_clone_obj',
	keyCornerTrimMesh: 'corner_trims_meshes',
	keyCornerTrimData: 'corner_trim',
	keyWallData: 'wall_raw',
	keyWallDataInterior: 'interior_wall_raw',
};

export type GablePedimentSideConfig = {
	side: string;
	anchorSide: string;
	rotationSide: string;
	surfaceDirection: number;
};

export const gablePedimentSideConfigs: Record<string, GablePedimentSideConfig> = {
	porch_front: {
		side: 'porch_front',
		anchorSide: 'porch',
		rotationSide: 'porch',
		surfaceDirection: 1,
	},
	front: {
		side: 'front',
		anchorSide: 'front',
		rotationSide: 'front',
		surfaceDirection: 1,
	},
	back: {
		side: 'back',
		anchorSide: 'back',
		rotationSide: 'back',
		surfaceDirection: -1,
	},
};

export const atticModel = {
	leanToWidth: 7.833, // 94"
	aFrameLeanToRoof: 0.208, // 2.5/12
	leanToHeight: 0, // 2.5/12
	secondFloorHeight: 7, // 84"
	porchWidth: 7.5, // 7'5"
	aFramePorchRoof: 0.208, // 2.5/12
	porchOffsetHeight: 0.583,
	barnOffsetHeight: 0.144,
	porchHeight: 0,
	barnLeanToRoof: 0.292, // 3.5/12
	barnPorchRoof: 0.208, // 2.5/12
	barnLeanToSpace: 3,
	overhang: 4.5,
	exteriorOffset: 0.4,
};

export const aFrameSingleGableHardCode = {
	width: 12,
	depth: 20,
	dormerDepth: 10,
	offsetRoof: 0.583,
};

export const leanToFrontPorchOffsetTop = 0.5;

export const offsetTop = 1;
export const offsetBottom = {
	level1: 0.4,
	level2: 0.44,
	level3: 0.45,
	level4: 0.24,
};

export const aframeCabin = {
	porchOffsetHeight: 0.51,
};

export const componentTypeSettings = {
	door: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	garage_door: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	ramp: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	windows: {
		anchor: {
			horizontal: 'middle',
			vertical: 'wall',
		},
		moveable: {
			horizontal: true,
			vertical: true,
		},
	},
	skylight_window: {
		anchor: {
			horizontal: 'middle',
			vertical: 'roof',
		},
		moveable: {
			horizontal: true,
			vertical: true,
		},
	},
	vent: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building',
		},
		moveable: {
			horizontal: true,
			vertical: true,
		},
	},
	cupola: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	transom: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: false,
			vertical: false,
		},
	},
	door_transom: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: false,
			vertical: false,
		},
	},
	dormer: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	flush_dormer: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	shelf: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	workbench: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	switch: {
		anchor: {
			horizontal: 'middle',
			vertical: 'wall',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	outlet: {
		anchor: {
			horizontal: 'middle',
			vertical: 'wall',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	light: {
		anchor: {
			horizontal: 'middle',
			vertical: 'building_top',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	breakerbox: {
		anchor: {
			horizontal: 'middle',
			vertical: 'wall',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	flower_box: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
	shutters: {
		anchor: {
			horizontal: 'middle',
			vertical: 'ground',
		},
		moveable: {
			horizontal: true,
			vertical: false,
		},
	},
};

export const distanceLineVerticalTopOptions = {
	points: [new Vector3(0, 0, 0), new Vector3(0, 0, 0)],
	updatable: true,
	instance: null,
	colors: [new Color4(0, 0, 0, 1), new Color4(0, 0, 0, 1)],
};

export const distanceLineVerticalBottomOptions = {
	points: [new Vector3(0, 0, 0), new Vector3(0, 0, 0)],
	updatable: true,
	instance: null,
	colors: [new Color4(0, 0, 0, 1), new Color4(0, 0, 0, 1)],
};

export const distanceLineHorizontalLeftOptions = {
	points: [new Vector3(0, 0, 0), new Vector3(0, 0, 0)],
	updatable: true,
	instance: null,
	colors: [new Color4(0, 0, 0, 1), new Color4(0, 0, 0, 1)],
};

export const distanceLineHorizontalRightOptions = {
	points: [new Vector3(0, 0, 0), new Vector3(0, 0, 0)],
	updatable: true,
	instance: null,
	colors: [new Color4(0, 0, 0, 1), new Color4(0, 0, 0, 1)],
};

export const NOTICES = {
	itemNotFit: `Items that don't fit the selected wall have been removed. Please consider changing the shed size or choosing a smaller item.`,
	cannotMove: 'Minimum spacing required for structural strength.',
	requireDoor: `Building requires at least one door.`,
	windowNotAdd: `Window cannot be added to building due to size compatibility. Consider a smaller window please.`,
	itemTooLarge: `Item is too large and has been removed. Rearrange the existing items for more space or try a smaller one.`,
	itemConflictWithTackRoom: `Items replaced by tackroom preset options.`,
	sideNotAccept: `Wall do not accept additional components.`,
	selectComponent: 'Please select the component.',
	cannotAddToCart: 'A shed can only be added to Cart if it has at least one door.',
	webGLNotSupport: `Model cannot be rendered due to WebGL service interruption. Reset or enable hardware acceleration in Chrome to fix this issue. <a href="https://shedpro.co/troubleshooting-chrome-service-error-for-3d-model-rendering/" target="_blank">See instructions.</a>`,
	doorRqdForWDWPos: `Building needs a door for proper window placement at fixed height.`,
	allowOneComponent: `This wall only allows one component to be added.`,
	cannotDetectSiding:
		'Cannot detect target siding for component customizations. Please rotate the model a little more, so your desired siding is prominently shown.',
	cannotAddComponent: 'Cannot add new component to this side.',
	missingInformation:
		'Unable to proceed with this order due to missing information. Please check to make sure you select custom option properly.',
	cannotNull: 'Custom line item name cannot be null.',
	shedDetailsUpdated: 'Shed details and price updated.',
	preventComponent: `This component can not add to here. Common reasons are due to restriction rules for siding materials or required spacing for equipment set.`,
	size: `Size restriction`,
	sidingMaterial: `Siding material restriction`,
	roofPitch: `Roof pitch restriction`,
	addOn: `Add on restriction`,
	sideSize: `Side & size restriction`,
	tackdoorCantMove: `Tackroom door cannot be moved.`,
	tackdoorMoveOutSide: `Tack door cannot be moved outside of tackroom wall.`,
	itemNotChange: `The item is fixed for this layout and cannot be changed.`,
	featureNotSupport: `Option not available for selected size.`,
	preventChangeComponent: `We cannot switch between components with different behavior.`,
	cannotPlaceVent: `Cannot place this vent because its required position on the model is occupied. Please move existing items to continue.`,
	loftNotCompatible: `Loft removed due to component incompatibility. Please adjust the component or choose another loft.`,
	saveCustomTax: 'Save custom tax success.',
	centerItemAllSides: 'All items have been distributed to all sides.',
	centerItemSelectedSides: 'All items have been distributed to the selected sides.',
	maxOneOpeningDoor:
		'Building only allows max 1 panel with door. Proceed to add panel with door to this side and remove panel with door on the other side?',
};

export const endGable2D = [
	'front',
	'back',
	'porch',
	'inner_front',
	'inner_back',
	'lean_to_back',
	'lean_to_front',
	'left_inner_porch',
	'right_inner_porch',
	'top_front',
	'top_back',
];
export const sideGable2D = [
	'right',
	'left',
	'inner_left',
	'left_back',
	'left_front',
	'front_inner_porch',
	'inner_right',
	'dormer',
	'top_left',
	'top_right',
	'lean_to_right',
];
export const reverseSides2D = [
	'back',
	'left',
	'inner_left',
	'left_back',
	'left_front',
	'front_inner_porch',
	'dormer',
	'top_left',
	'inner_back',
	'lean_to_back',
	'right_inner_porch',
	'top_back',
];
export const ePackageType = ['switch', 'outlet', 'light', 'breakerbox'];
export const loftSides = ['front', 'center_gable', 'back', 'left', 'center_side', 'right'];
export const zSide = [
	'left',
	'top_left',
	'right',
	'top_right',
	'inner_left',
	'left_front',
	'left_back',
	'front_inner_porch',
	'left_middle_porch',
	'inner_right',
	'lean_to_right',
	'roof_left',
	'roof_right',
	'roof',
	'dormer',
];
export const positiveSide = [
	'front',
	'inner_front',
	'left',
	'inner_left',
	'left_front',
	'left_back',
	'front_inner_porch',
	'right_inner_porch',
	'lean_to_front',
	'top_front',
	'top_left',
	'porch',
	'roof_left',
	'floor',
	'roof',
];
export const typesCutFrame = ['door', 'windows', 'vent', 'transom'];
export const layoutTypes = [
	'door',
	'windows',
	'vent',
	'shelf',
	'workbench',
	'skylight_window',
	'misc_interior',
	'misc_exterior',
];
export const typeHasColor = ['door', 'windows', 'vent', 'shutters', 'flower_box', 'misc_interior'];
export const interiors = [
	'shelf',
	'workbench',
	'switch',
	'outlet',
	'light',
	'breakerbox',
	'misc_interior',
	'stair',
];
export const colorLabels = {
	door: 'Door color',
	windows: 'Window color',
	vent: 'Vent color',
	shutters: 'Shutter color',
	flower_box: 'Flower Box color',
	countertops: 'Countertops color',
	appliance: 'Appliance color',
};

export const API_END_POINT = {
	SHED: '/api/shed/',
};

export const innerObjectName = {
	origin: 'new_inner_obj',
	cut: 'new_inner_cut_obj',
};

export const wallFinishName = {
	origin: 'wall_finish_obj',
	cut: 'wall_finish_cut_obj',
};

export const wallFinish = {
	mat1: 'wall_finish_mat_1',
	mat2: 'wall_finish_mat_2',
	mat3: 'wall_finish_mat_3',
	mat4: 'wall_finish_mat_4',
};

export const convertSides = { front: 'left', left: 'back', back: 'right', right: 'front' };
export const revertSides = { front: 'right', left: 'front', back: 'left', right: 'back' };

export const SWAP_SIDE_DELUXE_PORCH = {
	diagonal_left: 'diagonal_right',
	diagonal_right: 'diagonal_left',
	inner_right: 'inner_left',
	inner_left: 'inner_right',
	right: 'left',
	left: 'right',
};

export const SWAP_SIDE_L_SHAPE_PORCH = {
	inner_right: 'inner_left',
	inner_left: 'inner_right',
	left: 'right',
	right: 'left',
};

export const SWAP_SIDE_CORNER_PORCH = {
	front: 'back',
	inner_front: 'inner_back',
	back: 'front',
	inner_back: 'inner_front',
};

export const sideReverseShape = ['back', 'right'];

export const sizePlusX = [
	'left',
	'left_back',
	'left_front',
	'front_inner_porch',
	'inner_left',
	'top_left',
];
export const sizePlusZ = ['back', 'left_inner_porch', 'inner_back'];
export const roofSide = [
	'roof_left',
	'roof_right',
	'roof',
	'lower_roof_left',
	'lower_roof_right',
	'upper_roof_left',
	'upper_roof_right',
];
export const subComponentTypes = ['ramp', 'shutters', 'shutter', 'flower_box'];

export const colorCategories = [
	'siding',
	'trim',
	'roof',
	'shutter',
	'door',
	'vent',
	'windows',
	'wainscoting',
	'rails',
	'countertops',
	'wall',
	'appliance',
	'ceiling',
	'accent_gable',
];

export const deluxePorchFlippedBalusterConfig = {
	right: {
		railingPosition: 'right',
		requiredRail: 'rail_v_l_obj',
		braceEnabled: 'column_braces_side_end_obj',
		braceDisabled: 'column_side_end_obj',
	},
	front: {
		railingPosition: 'front',
		requiredRail: 'rail_h_r_obj',
		braceEnabled: 'column_braces_gable_end_obj',
		braceDisabled: 'column_gable_end_obj',
	},
	left: {
		railingPosition: 'left',
		requiredRail: 'rail_v_r_obj',
		braceEnabled: null,
		braceDisabled: null,
	},
};

export const DELUXE_PORCH_FLIPPED_BOTTOM_OFFSET = 0.24;

export const EMPTY_BALUSTER_OFFSET = { x: 0, z: 0 };

export const PAVILLIONS_POST_DISTANCE = 4;

export const PAVILION = {
	roofBoneOffset: 0.233, // Distance from bone roof to the actual point
	gableEndFrameThickness: 0.567, // The thickness of the frame member running along the gable end direction.
};

export const COMPONENT_SIZE_FULL_WALL = 'full-wall';

export const INTERIOR_DIAGONAL_SIDING_OFFSET = 0.14;

export const POST_DISTANCE = {
	DEFAULT: 15,
	BACKYARDBEYOND_HORSE_SHELTER: 8,
};

export const MIN_LENGTH_FOR_DIVIDER_POST = 12;

export const GAMBREL_ROOF_OVERHANG_FRONT_BONES = [
	'r_f_l1',
	'r_f_l2',
	'r_f_r1',
	'r_f_r2',
	'r_f_t',
	'roof_standing_seam_barn_f_l1',
	'roof_standing_seam_barn_f_l2',
	'roof_standing_seam_barn_f_t',
	'roof_standing_seam_barn_f_r2',
	'roof_standing_seam_barn_f_r1',
];

export const GAMBREL_ROOF_OVERHANG_BACK_BONES = [
	'r_b_l1',
	'r_b_l2',
	'r_b_r1',
	'r_b_r2',
	'r_b_t',
	'roof_standing_seam_barn_b_l1',
	'roof_standing_seam_barn_b_l2',
	'roof_standing_seam_barn_b_t',
	'roof_standing_seam_barn_b_r2',
	'roof_standing_seam_barn_b_r1',
];

export const GAMBREL_ROOF_OVERHANG_LEFT_BONES = [
	'r_b_l1',
	'r_f_l1',
	'rm_l1',
	'roof_standing_seam_barn_b_l1',
	'roof_standing_seam_barn_f_l1',
	'roof_standing_seam_barn_metal_b_l1',
];

export const GAMBREL_ROOF_OVERHANG_RIGHT_BONES = [
	'r_b_r1',
	'r_f_r1',
	'rm_r1',
	'roof_standing_seam_barn_b_r1',
	'roof_standing_seam_barn_f_r1',
	'roof_standing_seam_barn_metal_b_r1',
];

export const STRUCTURE_COTTAGE_OVERHANG_OFFSET = {
	OVERHANG_X: 1.25,
	OVERHANG_Y: 0.5,
};

export const AFRAME_ROOF_OVERHANG_FRONT_BONES = [
	'r_f_l1',
	'r_f_t',
	'r_f_r1',
	'roof_permatile_aframe_f_l1',
	'roof_permatile_aframe_f_r1',
	'roof_permatile_aframe_f_t',
	'roof_standing_seam_aframe_f_l1',
	'roof_standing_seam_aframe_f_r1',
	'roof_standing_seam_aframe_f_t',
	'roof_double_s_shape_f_l1',
	'roof_double_s_shape_f_t',
	'roof_double_s_shape_f_r1',
];

export const AFRAME_ROOF_OVERHANG_BACK_BONES = [
	'r_b_l1',
	'r_b_t',
	'r_b_r1',
	'roof_permatile_aframe_b_l1',
	'roof_permatile_aframe_b_r1',
	'roof_permatile_aframe_b_t',
	'roof_standing_seam_aframe_b_l1',
	'roof_standing_seam_aframe_b_r1',
	'roof_standing_seam_aframe_b_t',
	'roof_double_s_shape_b_l1',
	'roof_double_s_shape_b_r1',
	'roof_double_s_shape_b_t',
];

export const AFRAME_ROOF_OVERHANG_LEFT_BONES = [
	'r_b_l1',
	'r_f_l1',
	'rm_l1',
	'roof_permatile_aframe_b_l1',
	'roof_permatile_aframe_f_l1',
	'roof_permatile_aframe_metal_l1',
	'roof_standing_seam_aframe_b_l1',
	'roof_standing_seam_aframe_f_l1',
	'roof_standing_seam_aframe_metal_l1',
	'roof_double_s_shape_b_l1',
	'roof_double_s_shape_f_l1',
	'pavilion_rafter_cloned_l',
];

export const AFRAME_ROOF_OVERHANG_RIGHT_BONES = [
	'r_b_r1',
	'r_f_r1',
	'rm_r1',
	'roof_permatile_aframe_b_r1',
	'roof_permatile_aframe_f_r1',
	'roof_permatile_aframe_metal_r1',
	'roof_standing_seam_aframe_b_r1',
	'roof_standing_seam_aframe_f_r1',
	'roof_standing_seam_aframe_metal_r1',
	'roof_double_s_shape_b_r1',
	'roof_double_s_shape_f_r1',
	'pavilion_rafter_cloned_r',
];

export const lShapePorchBalusterConfig = {
	right: {
		railingPosition: 'right',
		requiredRail: 'rail_v_r_obj',
		braceEnabled: 'column_braces_side_end_obj',
		braceDisabled: 'column_side_end_obj',
	},
	front: {
		railingPosition: 'front',
		requiredRail: 'rail_h_l_obj',
		braceEnabled: 'column_braces_gable_end_obj',
		braceDisabled: 'column_gable_end_obj',
	},
	left: {
		railingPosition: 'left',
		requiredRail: 'rail_v_l_obj',
		braceEnabled: null,
		braceDisabled: null,
	},
};

export const lShapePorchRightBalusterConfig = {
	right: {
		railingPosition: 'right',
		requiredRail: 'rail_v_r_obj',
		braceEnabled: null,
		braceDisabled: null,
	},
	front: {
		railingPosition: 'front',
		requiredRail: 'rail_h_r_obj',
		braceEnabled: 'column_braces_gable_end_obj',
		braceDisabled: 'column_gable_end_obj',
	},
	left: {
		railingPosition: 'left',
		requiredRail: 'rail_v_l_obj',
		braceEnabled: 'column_braces_side_end_obj',
		braceDisabled: 'column_side_end_obj',
	},
};

export const INCHES_PER_FOOT = 12;

export const LSHAPE_PORCH = {
	SANDBOX_PORCH_DEPTH: 6,
	SANDBOX_PORCH_WIDTH: 12,
	DEFAULT_PORCH_WIDTH: 8,
	DEFAULT_PORCH_DEPTH: 4,
};

export const LEANTO_GREENHOUSE_STATIC_MATERIALS = ['siding_fb_02_mat', 'siding_lr_02_mat'];

export const ADD_COMPONENT_POPUP_SIZE = {
	WIDTH: 410,
	HEIGHT: 650,
};

export const MONITOR_BARN_NOT_ALLOWED_VERSIONS = [
	'structure_rancher_modular_add_roof_standing_seam',
	'structure_monitorbarns_helmuth_update_UV_teststaging',
	'structure_overlander',
	'structure_rancher_modular',
	'structure_monitorbarns_helmuth_update_UV',
	'structure_monitorbarns_helmuth',
];

export const ohioChickenCoopOverhangFixed = 2.827;

export const cameraSideGroups: { [key: string]: { cameraAlpha: number; sides: string[] } } = {
	frontGroup: {
		cameraAlpha: Math.PI / 2,
		sides: [
			'front',
			'porch',
			'diagonal_right',
			'diagonal_left',
			'inner_front',
			'top_front',
			'lean_to_front',
		],
	},
	rightGroup: {
		cameraAlpha: 0,
		sides: ['right', 'inner_right', 'top_right', 'lean_to_right'],
	},
	backGroup: {
		cameraAlpha: -Math.PI / 2,
		sides: ['back', 'inner_back', 'top_back', 'lean_to_back'],
	},
	leftGroup: {
		cameraAlpha: Math.PI,
		sides: [
			'left',
			'dormer',
			'inner_left',
			'top_left',
			'left_front',
			'left_back',
			'front_inner_porch',
			'left_middle_porch',
		],
	},
	leftInnerPorchGroup: {
		cameraAlpha: (5 * Math.PI) / 4,
		sides: ['left_inner_porch'],
	},
	rightInnerPorchGroup: {
		cameraAlpha: (3 * Math.PI) / 4,
		sides: ['right_inner_porch'],
	},
};

export const DOUBLE_S_SHAPE = 'double_s_shape';

export const BALUSTER_SIDE_PORCH_OFFSET = 0.2;

export const BALUSTER_FRONT_PORCH_OFFSET = 0.2;

export const CLASS_HIDDEN_PREVENT_COMPONENT_ITEM = 'hidden-prevent-component-item';

export const CLASS_CUSTOM_SIZE_HIDDEN = 'custom-size-hidden';

export const CLASS_HIDDEN_DOOR_PANEL_ITEM = 'hidden-door-panel';

export const CLASS_HIDDEN_BY_WL = 'hidden-by-wl';

export const CLASS_DISABLE_BY_WL = 'disable-by-wl';

export const CLASS_DISABLE_BY_ZONE = 'disable-by-zone';

export const CLASS_DISABLE_BY_LAYOUT = 'disable-by-layout';

export const CLASS_DISABLE_BY_STRUCTURE_COTTAGE_SCOUT = 'disable-by-structure-cottage-scout';

export const CLASS_DISABLE_BY_LOFTED_HORSERUNIN = 'disable-by-structure-lofted-horserunin';

export const CLASS_CUSTOM_SELECT_DISABLED = 'custom-select-disabled';

export const FIXED_SCREEN_SIZE_FACTOR = 0.05;
export const FIXED_SCREEN_SIZE_FACTOR_MOBILE = 0.1;
/**
 * Filter door components by layout and side.
 * Config: layoutId -> side -> allowed doors. '*' = all sides fallback.
 */
export const LAYOUT_DOOR_FILTER: Record<string, Record<string, string[]>> = {
	scout: {
		'*': ['door_panel_with_wire_opening_60x72'],
	},
	outpost: {
		front: ['door_panel_with_wire_opening_60x72', 'door_panel_with_wire_fix_60x72'],
		left: [
			'door_panel_with_wire_fix_120x72',
			'door_panel_with_wire_left_opening_120x72',
			'door_panel_with_wire_right_opening_120x72',
		],
		right: [
			'door_panel_with_wire_fix_120x72',
			'door_panel_with_wire_left_opening_120x72',
			'door_panel_with_wire_right_opening_120x72',
		],
	},
	basecamp: {
		'*': ['door_panel_with_wire_opening_60x72', 'door_panel_with_wire_fix_60x72'],
	},
};

export const PANELS_DOOR = [
	'door_panel_with_wire_opening_60x72',
	'door_panel_with_wire_fix_60x72',
	'door_panel_with_wire_fix_120x72',
	'door_panel_with_wire_left_opening_120x72',
	'door_panel_with_wire_right_opening_120x72',
];

export const PANELS_WITH_DOOR = [
	'door_panel_with_wire_opening_60x72',
	'door_panel_with_wire_left_opening_120x72',
	'door_panel_with_wire_right_opening_120x72',
];

export const PANELS_NO_DOOR = ['door_panel_with_wire_fix_60x72', 'door_panel_with_wire_fix_120x72'];

/**
 * Dog kennel layout IDs
 */
export const DOG_KENNEL_LAYOUT_ID = {
	OUTPOST: 'outpost',
	SCOUT: 'scout',
	BASECAMP: 'basecamp',
} as const;

export const DOG_KENNEL_DEPTH = {
	outpost: 4,
	scout: 4,
	basecamp: 9,
};

export const CUSTOM_SIZE_HARDCODE_MODEL_MESSAGE =
	'This model supports fixed sizes only and cannot be built with custom dimensions. Please choose a different model or contact ShedPro if you require a custom-sized building.';

export const DOG_KENNEL_SIDING_NAME = 'siding_dog_kennel';

export const WALL_BONE_OVERRIDES = {
	structure_lean_to_with_front_porch: {
		front: ['b_f_l1', 'b_f_l2', 'b_f_r2', 'b_f_r1'],
		back: ['b_b_r1', 'b_b_r2', 'b_b_l2', 'b_b_l1'],
	},
	structure_aframe_metal_frontporch: {
		left: ['b_b_l1', 'b_b_l2', 'b_f_l2', 'b_f_l1'],
		right: ['b_f_r1', 'b_f_r2', 'b_b_r2', 'b_b_r1'],
	},
};

export const DEFAULT_OVERHANG_ZERO = {
	front: 0,
	back: 0,
	left: 0,
	right: 0,
};

export const MONTANA = {
	SUFFIX: '__second_model',

	AFRAME_DEPTH: 12,

	MAIN_ROOF_PITCH: 12 / 12,
	MAIN_WALL_HEIGHT: 3.25,

	SECOND_WALL_HEIGHT: 7.333,
};

export const GROUP_CLASS_PORCH_CONFIG = [
	'group-upgrade-porch-config',
	'group-upgrade-porch-config-depth',
	'group-upgrade-porch-config-width',
	'group-upgrade-porch-config-alley_width',
	'group-upgrade-porch-config-side_wall_width',
	'group-upgrade-porch-config-diagonal_width',
	'group-upgrade-porch-config-diagonal_depth',
	'group-upgrade-porch-config-position',
];

export const PORCH_CONFIG_STEP = {
	front: ['depth'],
	side: ['depth'],
	middle: ['depth', 'width'],
	corner: ['depth', 'width', 'position'],
	l_shape: ['depth', 'alley_width', 'side_wall_width', 'position'],
};

export const GROUP_CLASS_BY_TYPE = {
	depth: 'group-upgrade-porch-config-depth',
	width: 'group-upgrade-porch-config-width',
	alley_width: 'group-upgrade-porch-config-alley_width',
	side_wall_width: 'group-upgrade-porch-config-side_wall_width',
	diagonal_width: 'group-upgrade-porch-config-diagonal_width',
	diagonal_depth: 'group-upgrade-porch-config-diagonal_depth',
	position: 'group-upgrade-porch-config-position',
};

export enum ACTION_SOURCES {
	ModelResize = 'MODEL_RESIZE',
}

export const PORCH_PRESET = {
	openingGroup: '.group-porch-preset-opening',
	railingGroup: '.group-porch-preset-railing',
	openingTab: '.porch-preset-opening-tab',
	openingPanel: '.porch-preset-opening-panel',
	openingSize: '.porch-preset-opening-size',
	openingPosition: '.porch-preset-opening-position',
	openingOffset: '.porch-preset-opening-offset',
	presetRailingName: '[name="ssb-group-porch-preset-railing"]',
	presetName: '[name="ssb-group-porch-preset"]',
	isEditableClass: 'is-editable',
	activeClass: 'active',
	openingFieldError: '.opening-field-error',
	openingFieldErrorVisibleClass: 'is-visible',
};

export const TRIM_TYPE_CONFIG: { type: string; category: string }[] = [
	{ type: 'windows', category: 'window_trim' },
	{ type: 'door', category: 'door_trim' },
];
